import { t } from "@lingui/core/macro";
import { isDefined } from "@regrello/core-utils";
import type { FieldFields, PartyBaseFields, SpectrumFieldVersionFields } from "@regrello/graphql-api";
import { getBasePartyTypeUnion, getPartyEmail } from "../../../../../../utils/parties/partyUtils";

export interface ValidationResult {
  info?: Array<{
    message: string;
    level?: "error" | "warning" | "info";
  }>;
  value?: string;
}

export function normalizeSelectValue(value: string): string {
  return value.toLocaleLowerCase();
}

/**
 * Validates that the provided value is non-empty.
 * If the value is empty, a ValidationResult is returned with an optional error set, depending on the required state.
 *
 * @param value - the value to check if present
 * @param isRequired - whether the value is required
 * @returns an optional validation result, if the value was empty
 */
export function validateRequired(value: string, isRequired: boolean): ValidationResult | null {
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0) {
    return {
      value,
      info: isRequired
        ? [
            {
              message: t`Required`,
              level: "error",
            },
          ]
        : undefined,
    };
  }

  return null;
}

/**
 * Extracts the allowed values from the field, using the versioned allowed values if available.
 *
 * For translatable allowed values, this returns the stringValues (pre-translation), not the display values.
 *
 * @param field - The field to extract the allowed values from.
 * @param fieldVersion - The version of the field to use to extract the allowed values from. Supersedes {@see field} if provided.
 * @returns The allowed-value strings from the field.
 */
export function extractAllowedValues(field: FieldFields, fieldVersion?: SpectrumFieldVersionFields) {
  if (fieldVersion?.allowedValues == null) {
    console.warn(
      "Unable to extract allowed values field version because it was not defined. " +
        "Falling back to the field's allow values, but this may be incorrect for the field version!",
    );
  }

  const allowedValueStrings: string[] = (
    fieldVersion?.allowedValues != null ? fieldVersion.allowedValues : field.allowedValues
  )
    .map((allowedValue: { stringValue?: string | null; partyValue?: PartyBaseFields | null }) =>
      allowedValue.partyValue != null
        ? getPartyEmail(getBasePartyTypeUnion(allowedValue.partyValue))
        : allowedValue.stringValue,
    )
    .filter(isDefined);

  return allowedValueStrings;
}
